"use client";

import { useQuery } from "@tanstack/react-query";
import { BookOpen, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";
import { useUpdateFolderStudySets } from "../api/use-update-folder-studysets";
import useAddStudySetsToFolderModal from "../hooks/use-add-studysets-to-folder-modal";

export const ManageStudySetsForm = () => {
  const t = useI18n();
  const { onClose, folderId } = useAddStudySetsToFolderModal();
  const trpc = useTRPC();

  const { data: allStudySets, status: studySetsStatus } = useQuery(
    trpc.studysets.get.queryOptions(
      {},
      { staleTime: Number.POSITIVE_INFINITY },
    ),
  );
  const studySets = useMemo(
    () => allStudySets?.filter((s) => s.type === "personal") ?? [],
    [allStudySets],
  );
  const { data: folder, status: folderStatus } = useQuery(
    trpc.folders.getStudySets.queryOptions(
      { id: folderId ?? "" },
      { enabled: !!folderId, staleTime: Number.POSITIVE_INFINITY },
    ),
  );
  const updateMutation = useUpdateFolderStudySets();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initial, setInitial] = useState<Set<string>>(new Set());

  const initialIds = useMemo(
    () => new Set(folder?.studySets.map((s) => s.id) ?? []),
    [folder],
  );

  useEffect(() => {
    setSelected(new Set(initialIds));
    setInitial(new Set(initialIds));
  }, [initialIds]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasChanges = () => {
    if (selected.size !== initial.size) return true;
    return [...selected].some((id) => !initial.has(id));
  };

  const handleSubmit = () => {
    if (!folderId) return;

    const studySetsToAdd = [...selected].filter((id) => !initial.has(id));
    const studySetsToRemove = [...initial].filter((id) => !selected.has(id));

    updateMutation.mutate(
      {
        id: folderId,
        studySetsToAdd,
        studySetsToRemove,
      },
      {
        onSuccess: () => onClose(),
      },
    );
  };

  const isLoading = studySetsStatus === "pending" || folderStatus === "pending";
  const isError = studySetsStatus === "error" || folderStatus === "error";

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="size-8 animate-spin mb-2" />
        <p>{t("loadingStudySets")}</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-destructive">
        <p className="font-medium">{t("somethingWentWrong")}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {t("pleaseTryAgainLater")}
        </p>
        <Button variant="outline" className="mt-4" onClick={onClose}>
          {t("close")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {!studySets.length ? (
          <div className="py-8 w-full border border-dashed rounded-2xl px-2">
            <div className="flex flex-col items-center justify-center w-full gap-3 h-full">
              <div className="size-12 grid place-items-center rounded border bg-muted/40">
                <BookOpen className="size-6" />
              </div>
              <div className="flex flex-col justify-center items-center gap-1 text-center">
                <h3 className="font-semibold text-base leading-6">
                  {t("noStudySetsCreatedYet")}
                </h3>
                <p className="text-sm font-normal leading-5 text-muted-foreground">
                  {t("noStudySetsInLibrary")}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <ScrollArea className="h-[280px] pr-4">
            <div className="grid gap-2">
              {studySets.map((studySet) => (
                <label
                  key={studySet.id}
                  htmlFor={`studyset-${studySet.id}`}
                  className="group flex items-center space-x-3 rounded-lg border p-3 hover:bg-accent transition-colors cursor-pointer w-full min-w-0"
                >
                  <Checkbox
                    id={`studyset-${studySet.id}`}
                    checked={selected.has(studySet.id)}
                    onCheckedChange={() => toggle(studySet.id)}
                  />
                  <span className="text-sm font-medium truncate min-w-0 flex-1">
                    {studySet.name}
                  </span>
                </label>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      <div className="flex flex-col-reverse gap-y-4 md:gap-y-0 md:flex-row w-full md:items-center md:justify-between">
        <Button type="button" variant="ghost" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!hasChanges() || updateMutation.isPending}
        >
          {updateMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {updateMutation.isPending ? t("saving") : t("saveChanges")}
        </Button>
      </div>
    </div>
  );
};
